// Reusable display primitives

export const CBNBadge = ({ sections = [] }) => (
  <div data-testid="cbn-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-navy/15 bg-navy/4 text-navy text-[11px] font-medium rounded-full tracking-tight">
    <span className="w-1 h-1 rounded-full bg-navy" />
    CBN {sections.join(" · ")}
  </div>
);

export const AtomBadge = ({ model, version, description, latency }) => (
  <div data-testid={`atom-badge-${model?.toLowerCase()}`} title={description || ""} className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-amber/30 bg-amber/8 text-amber-600 text-[11px] rounded-md">
    <span className="w-1 h-1 rounded-full bg-amber-600" />
    <span className="font-medium tracking-tight">{model}</span>
    <span className="text-amber-600/80">{version}</span>
    {latency && <span className="text-amber-600/70 ml-1">· {latency}</span>}
  </div>
);

const RISK_STYLES = {
  Low: "bg-risk-low/10 text-risk-low border-risk-low/30",
  Medium: "bg-risk-medium/10 text-risk-medium border-risk-medium/30",
  High: "bg-risk-high/10 text-risk-high border-risk-high/30",
  "Very High": "bg-risk-veryhigh/10 text-risk-veryhigh border-risk-veryhigh/30",
};

export const RiskPill = ({ band, score, size = "md" }) => (
  <span data-testid={`risk-pill-${band?.toLowerCase().replace(' ','-')}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${size === 'sm' ? 'text-[10px]' : 'text-[11px]'} font-medium rounded ${RISK_STYLES[band] || RISK_STYLES.Low}`}>
    {band}{score != null ? ` · ${score}` : ""}
  </span>
);

const STATUS_STYLES = {
  "Cleared": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Pending review": "bg-risk-medium/10 text-risk-medium border-risk-medium/30",
  "In case": "bg-info/10 text-info border-info/30",
  "In review": "bg-risk-medium/10 text-risk-medium border-risk-medium/30",
  "Escalated": "bg-risk-high/10 text-risk-high border-risk-high/30",
  "MLRO reviewing": "bg-info/10 text-info border-info/30",
  "Awaiting MLRO review": "bg-info/10 text-info border-info/30",
  "Recommend SAR": "bg-risk-high/10 text-risk-high border-risk-high/30",
  "SAR drafted": "bg-info/10 text-info border-info/30",
  "SAR filed": "bg-risk-veryhigh/10 text-risk-veryhigh border-risk-veryhigh/30",
  "Acknowledged": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Draft": "bg-ink-muted/10 text-ink-muted border-ink-muted/30",
  "Filed": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Open": "bg-info/10 text-info border-info/30",
  "Confirmed suspicious": "bg-risk-veryhigh/10 text-risk-veryhigh border-risk-veryhigh/30",
  "Closed - cleared": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Active": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Dormant": "bg-ink-muted/10 text-ink-muted border-ink-muted/30",
  "Restricted": "bg-risk-high/10 text-risk-high border-risk-high/30",
  "Connected": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Operational": "bg-risk-low/10 text-risk-low border-risk-low/30",
  "Pending configuration": "bg-risk-medium/10 text-risk-medium border-risk-medium/30",
};

export const StatusPill = ({ status }) => (
  <span data-testid={`status-pill`} className={`inline-flex items-center px-1.5 py-0.5 border text-[11px] rounded ${STATUS_STYLES[status] || "bg-ink-muted/10 text-ink-muted border-ink-muted/30"}`}>
    {status}
  </span>
);

export const PriorityPill = ({ priority }) => {
  const styles = { P1: "bg-risk-veryhigh text-white", P2: "bg-risk-high text-white", P3: "bg-risk-medium text-white", P4: "bg-ink-muted text-white" };
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded ${styles[priority] || styles.P4}`}>{priority}</span>;
};

export const PEPBadge = ({ pep }) => pep ? (
  <span className="inline-flex items-center px-1.5 py-0.5 bg-amber/12 border border-amber/40 text-amber-600 text-[10px] font-medium rounded">PEP</span>
) : null;

export const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`bg-bg-surface border border-line rounded-xl shadow-[0_1px_2px_rgba(11,43,92,0.04)] ${className}`}>{children}</div>
);

export const CardHeader = ({ title, subtitle, right, testid }) => (
  <div data-testid={testid} className="px-5 py-3.5 border-b border-line flex items-center justify-between gap-3">
    <div>
      <div className="text-[13px] font-medium text-ink tracking-tight display">{title}</div>
      {subtitle && <div className="text-[11px] text-ink-subtle mt-0.5">{subtitle}</div>}
    </div>
    {right && <div>{right}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, sections, atom, right, breadcrumb }) => (
  <div className="px-7 py-5 border-b border-line bg-bg-surface flex items-start justify-between gap-4">
    <div className="flex-1">
      {breadcrumb && <div className="text-[11px] text-ink-subtle mb-1.5">{breadcrumb}</div>}
      <h1 data-testid="page-title" className="display text-[22px] font-semibold leading-7 text-ink tracking-tight">{title}</h1>
      {subtitle && <p className="text-[13px] text-ink-muted mt-1">{subtitle}</p>}
      {atom && <div className="mt-2.5">{atom}</div>}
    </div>
    <div className="flex items-center gap-2 flex-wrap justify-end">
      {right}
      {sections && <CBNBadge sections={sections} />}
    </div>
  </div>
);

export const MetricTile = ({ label, value, sub, trend, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="text-left bg-bg-surface border border-line rounded-xl px-4 py-3.5 hover:border-line-strong hover:bg-bg-warm hover:-translate-y-[1px] transition-all w-full group shadow-[0_1px_2px_rgba(11,43,92,0.04)] hover:shadow-[0_4px_12px_rgba(11,43,92,0.08)]">
    <div className="text-[10px] text-ink-subtle uppercase tracking-[0.06em] font-medium">{label}</div>
    <div className="display text-[26px] font-semibold tabular mt-1.5 leading-tight tracking-tightest text-ink">{value}</div>
    {sub && <div className="text-[11px] text-ink-muted mt-1.5">{sub}</div>}
    {trend != null && <div className={`text-[11px] mt-1.5 font-medium ${trend >= 0 ? 'text-risk-low' : 'text-risk-high'}`}>{trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}% wow</div>}
  </button>
);

export const EmptyState = ({ message = "No records match the current filters.", action }) => (
  <div className="py-12 px-6 text-center">
    <div className="text-[13px] text-ink-muted">{message}</div>
    {action && <div className="mt-3">{action}</div>}
  </div>
);
